import { MixState, STEMS, StemName, audibleVolume } from '../lib/studioEngine';
import { STEM_META } from '../lib/format';
import { STEM_COLOR } from './studioIcons';

interface Props {
  mix: MixState;
  emptyStems?: Set<StemName>;
  activeStem?: StemName;
  onChange: (next: MixState) => void;
}

// 6-row mixer that mirrors the extension's in-page bar, restyled for the studio
// console: each stem gets its own color (the slider fill + thumb), and the track
// is always visible in both themes.
export function Mixer({ mix, emptyStems, activeStem, onChange }: Props) {
  const anySolo = STEMS.some((s) => mix.stems[s].solo);

  function patchStem(stem: StemName, patch: Partial<MixState['stems'][StemName]>) {
    onChange({
      ...mix,
      stems: { ...mix.stems, [stem]: { ...mix.stems[stem], ...patch } },
    });
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(80px, 120px) 1fr 52px 30px 30px',
        gap: '18px 12px',
        alignItems: 'center',
        padding: '7px 0',
      }}
    >
      {STEMS.map((stem) => {
        const s = mix.stems[stem];
        const empty = emptyStems?.has(stem) ?? false;
        const audible = audibleVolume(s, anySolo);
        const isAudible = audible > 0.005;
        const color = STEM_COLOR[stem] ?? 'var(--st-accent)';
        const meta = STEM_META[stem];
        const pct = Math.round(s.volume * 100);
        const active = activeStem === stem;

        return (
          <div key={stem} style={{ display: 'contents', ['--st-stem' as string]: color }} data-active={active ? 'true' : 'false'}>
            <div
              style={{
                color: 'var(--st-text)',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                minWidth: 0,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta.label}</span>
              {empty && <span style={{ fontSize: 12, fontWeight: 500, fontStyle: 'italic', color: 'var(--st-text-3)' }}>· silent</span>}
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={s.volume}
              onChange={(e) => patchStem(stem, { volume: parseFloat(e.target.value) })}
              className="st-mix-slider"
              aria-label={`${meta.label} volume`}
              style={{ accentColor: color }}
            />
            <div
              className="st-mono"
              style={{
                color: s.volume > 0.005 ? 'color-mix(in oklch, var(--st-stem) 72%, var(--st-text))' : 'var(--st-text-3)',
                fontSize: 13,
                textAlign: 'right',
              }}
            >
              {pct}%
            </div>
            <MixerBtn kind="solo" active={s.solo} onClick={() => patchStem(stem, { solo: !s.solo })} label={`Solo ${meta.label}`}>
              S
            </MixerBtn>
            <MixerBtn kind="mute" active={s.mute} onClick={() => patchStem(stem, { mute: !s.mute })} label={`Mute ${meta.label}`}>
              M
            </MixerBtn>
          </div>
        );
      })}

      <style>{`
        .st-mix-slider {
          width: 100%; height: 4px; cursor: pointer; margin: 0; padding: 0;
        }
        .st-mix-slider:focus-visible { outline: 2px solid color-mix(in oklch, var(--st-stem, var(--st-accent)) 55%, transparent); outline-offset: 2px; }
      `}</style>
    </div>
  );
}

function MixerBtn({
  kind,
  active,
  onClick,
  label,
  children,
}: {
  kind: 'solo' | 'mute';
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  const litColor = kind === 'solo' ? '#facc15' : '#ef4444';
  const hoverColor = kind === 'solo' ? '#eab308' : '#ef4444';

  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      style={{
        background: active ? `color-mix(in srgb, ${litColor} 10%, var(--st-surface))` : 'var(--st-surface)',
        border: `1px solid ${active ? litColor : 'var(--st-line-2)'}`,
        color: active ? litColor : 'var(--st-text-3)',
        width: 30,
        height: 30,
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 700,
        cursor: 'pointer',
        lineHeight: 1,
        padding: 0,
        transition: 'all 80ms ease',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.color = hoverColor;
          e.currentTarget.style.borderColor = hoverColor;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.color = 'var(--st-text-3)';
          e.currentTarget.style.borderColor = 'var(--st-line-2)';
        }
      }}
    >
      {children}
    </button>
  );
}
